/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import {
  useAuth,
  useGetSavedList,
  useHomeSearchParams,
  useI18N,
  usePostDeleteSavedList,
} from "common/hooks";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useAtom } from "jotai";
import {
  leftPanelExpandedAtom,
  mapVisibleOnMobileAtom,
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewDataChangedAtom,
  savedPlaceSortByToViewAtom,
  venueDetailsOpenedAtom,
} from "@/state/atoms";
import { savedPlaceIdToViewAtom } from "@/state/atoms";
import { useEffect, useRef, useState } from "react";
import AnalyticsService from "@/services/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import SavePlaceMenu from "@/components/common/SavedPlaces/SavePlaceMenu";
import { IoList } from "react-icons/io5";
import { FaRegMap } from "react-icons/fa6";
import { RWebShare } from "react-web-share";
import { Helmet } from "react-helmet";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import { useCustomToast } from "@/hooks/useCustomToast";
import MapView from "@/features/home/MapView";
import { cn } from "@/lib/utils";
import { ViewPager, Frame, Track, View } from "react-view-pager";
import VenueList from "@/features/home/VenueList";
import { ListBullets, MapTrifold } from "phosphor-react";
import VenueDetails from "@/features/home/VenueDetails";
import { Venue } from "common/types";

export const Route = createFileRoute(
  "/savedplacedetails/$savedPlaceId/user/$userId"
)({
  component: SavedPlaceDetails,
});

function SavedPlaceDetails() {
  const { query } = Route.useSearch() as any;

  const [isMapVisible, setIsMapVisible] = useAtom(mapVisibleOnMobileAtom);
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [, setSavedPlaceSortByToView] = useAtom(savedPlaceSortByToViewAtom);
  const [savedPlaceIdToViewDataChanged, setSavedPlaceIdToViewDataChanged] =
    useAtom(savedPlaceIdToViewDataChangedAtom);
  const [mapRef] = useState<google.maps.Map | null>(null);

  const [currentSort, setCurrentSort] = useState<
    "latest" | "earliest" | "alphabetical"
  >("latest");

  const { selectedVenue, setSelectedVenue } = useSelectedVenue();

  const { userId: userId, savedPlaceId: savedPlaceId } = Route.useParams();
  const isDark = useDarkMode();
  const { user, isLoading, isFetching, isLoggedIn } = useAuth();
  //const isAuthLoading = isLoading || isFetching;
  const i18n = useI18N();
  const navigate = useNavigate({ from: "/" });

  const showToast = useCustomToast();

  const isUserOwner = `${user?.id}` === userId ? true : false;

  const router = useRouter();
  const onBack = () => router.history.back();

  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);
  const [isLeftPaneVisible, setIsLeftPaneVisible] = useAtom(
    leftPanelExpandedAtom
  );
  //const isMapVisibleOnMobile = useAtomValue(mapVisibleOnMobileAtom);
  const checkScreenWidth = () => window.innerWidth <= 768;

  const track = useRef<Track>(null);
  const [currentVenueTypePage, setCurrentVenueTypePage] = useState(0);
  const [mobileListView, setMobileListView] = useState(false);

  // load saved places details from the server
  const {
    error: getSavedListError,
    isLoading: getSavedListLoading,
    savedList,
    refetch: getSavedListRefetch,
  } = useGetSavedList(savedPlaceId, userId);

  // Delete save place list
  const {
    deleteList,
    error: deleteListError,
    isLoading: isDeleteListLoading,
    result: deleteListResult,
  } = usePostDeleteSavedList();

  useEffect(() => {
    if (deleteListResult) {
      AnalyticsService.trackGAEventHandler("deleteListClickedListDeleted");
      AnalyticsService.trackMx("deleteListClickedListDeleted", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });

      //redirect to my saved places page
      navigate({ to: "/mysavedplaces" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteListResult]);

  useEffect(() => {
    if (deleteListError) {
      AnalyticsService.trackGAEventHandler("viewListErrorDeletingList");
      AnalyticsService.trackMx("viewListErrorDeletingList", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });
      showToast(deleteListError.message, "error");
    }
  }, [deleteListError]);

  const { setHomeSearchParams } = useHomeSearchParams();

  useEffect(() => {
    if (savedPlaceIdToViewDataChanged) {
      console.log(
        "savedPlaceIdToViewDataChanged",
        savedPlaceIdToViewDataChanged
      );
      setSavedPlaceIdToViewDataChanged(false);
      getSavedListRefetch();

      setHomeSearchParams((prev) => ({
        ...prev,
        saved_place_id: savedPlaceId,
        saved_place_user_id: userId,
        zoom: mapRef?.getZoom().toString() || "15",
        searchThisAreaClicked: "no",
      }));
    }
  }, [
    getSavedListRefetch,
    mapRef,
    savedPlaceId,
    savedPlaceIdToViewDataChanged,
    setHomeSearchParams,
    setSavedPlaceIdToViewDataChanged,
    userId,
  ]);

  useEffect(() => {
    // Function to handle screen resize
    const handleResize = () => {
      const isSmallScreen = checkScreenWidth();
      if (isSmallScreen) {
        setIsLeftPaneVisible(true);
      }
    };

    // Set initial visibility based on current screen width
    handleResize();

    // Add event listener to window
    window.addEventListener("resize", handleResize);

    // Clean up event listener
    return () => window.removeEventListener("resize", handleResize);
  }, [setIsLeftPaneVisible]);

  useEffect(() => {
    console.log("Dark mode is", isDark ? "enabled" : "disabled");
  }, [isDark]);

  useEffect(() => {
    setIsVenueDetailsOpen(false);
    setIsLeftPaneVisible(true);
  }, [setIsLeftPaneVisible, setIsVenueDetailsOpen]);

  useEffect(() => {
    if (savedPlaceId) {
      setSavedPlaceIdToView(savedPlaceId);
      setSavedPlaceByUserIdToView(userId);
      setSavedPlaceSortByToView(currentSort);

      setHomeSearchParams((prev) => ({
        ...prev,
        saved_place_id: savedPlaceId,
        saved_place_user_id: userId,
        zoom: mapRef?.getZoom().toString() || "15",
        searchThisAreaClicked: "no",
      }));
    }
  }, [
    currentSort,
    mapRef,
    savedPlaceId,
    setHomeSearchParams,
    setSavedPlaceByUserIdToView,
    setSavedPlaceIdToView,
    setSavedPlaceSortByToView,
    userId,
  ]);

  useEffect(() => {
    if (getSavedListError) {
      AnalyticsService.trackGAEventHandler("viewListErrorLoading");
      AnalyticsService.trackMx("viewListErrorLoading", {});

      showToast(getSavedListError.message, "error");

      setTimeout(() => {
        //redirect to my saved places page
        navigate({ to: "/mysavedplaces" });
      }, 500);
    }
  }, [getSavedListError, navigate]);

  const handleDeleteList = async () => {
    console.log("handleDeleteList");

    //Delete list
    deleteList({
      savedPlaceId: savedPlaceId,
    });
  };

  const handleListSort = (value) => {
    console.log("handleListSort value", value);

    if (value === "latest") {
      AnalyticsService.trackGAEventHandler(
        "viewListClickedSortSavedPlacesLatest"
      );
      AnalyticsService.trackMx("viewListClickedSortSavedPlacesLatest", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });
    } else if (value === "earliest") {
      AnalyticsService.trackGAEventHandler(
        "viewListClickedSortSavedPlacesEarliest"
      );
      AnalyticsService.trackMx("viewListClickedSortSavedPlacesEarliest", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });
    } else {
      AnalyticsService.trackGAEventHandler(
        "viewListClickedSortSavedPlacesAlphabetical"
      );
      AnalyticsService.trackMx("viewListClickedSortSavedPlacesAlphabetical", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });
    }

    setCurrentSort(value as any);
  };

  const handleListShare = () => {
    console.log("shared successfully!");
    AnalyticsService.trackGAEventHandler("viewListClickedShare");
    AnalyticsService.trackMx("viewListClickedShare", {
      SavedPlaceName: savedList?.saved_places[0].name,
      SavedPlaceId: savedList?.saved_places[0].id,
      SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
    });
  };

  const handleListSettings = () => {
    console.log("list settings!");
    AnalyticsService.trackGAEventHandler("viewListClickedSettings");
    AnalyticsService.trackMx("viewListClickedSettings", {
      SavedPlaceName: savedList?.saved_places[0].name,
      SavedPlaceId: savedList?.saved_places[0].id,
      SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
    });
  };

  const toggleMobileMap = () => {
    AnalyticsService.trackGAEventHandler("homeSwitchedToListView");
    AnalyticsService.trackMx("homeSwitchedToListView", {});
    setIsMapVisible(!isMapVisible);
  };

  useEffect(() => {
    if (selectedVenue) {
      onVenueClick(selectedVenue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue]);

  const onVenueClick = (location: Venue) => {
    console.log("onVenueClick", location);
    AnalyticsService.trackGAEventHandler("viewListClickedViewVenue");
    AnalyticsService.trackMx("viewListClickedViewVenue", {
      VenueId: location.id,
      VenueBrandId: location.brandId,
      VenueBrandName: location.brandName,
      VenueAddress: location.address,
      VenueName: location.name,
      VenueType: location.businessType,
    });
    track.current?.next();
    setCurrentVenueTypePage(1);
    setTimeout(() => {
      setSelectedVenue(location);
    }, 400);
  };

  return (
    <>
      <Helmet>
        <title>
          {savedPlaceId === "all"
            ? i18n.get("allSavedPlaces")
            : `${savedList?.saved_places[0]?.name} | Sociability`}
        </title>
      </Helmet>

      <div className="h-[calc(100vh-60px)] bg-white dark:bg-primary-900 flex">
        {/* DESKTOP VIEW */}
        <div className="hidden md:flex flex-row flex-1">
          <div
            className={cn(
              "transition duration-100 flex-1 flex flex-col pt-[20px] w-[50%] pb-[20px]",
              isLeftPaneVisible ? "flex" : "hidden"
            )}
          >
            {!selectedVenue && (
              <>
                {/* ROW 1 */}
                <div className="pl-[32px] pr-[20px] flex justify-between items-center">
                  {/* Left area */}
                  {isLoggedIn && isUserOwner && (
                    <div className="flex items-center">
                      <button
                        onClick={() => {
                          AnalyticsService.trackGAEventHandler(
                            "viewListClickedBackButton"
                          );
                          AnalyticsService.trackMx(
                            "viewListClickedBackButton",
                            {}
                          );
                          onBack();
                        }}
                        aria-label={`Back to ${i18n.get("mySavedPlaces")}`}
                        className="flex items-center gap-2 px-1 py-3 bg-white dark:bg-primary-900 text-center button-small-semi-bold !text-primary-500 dark:!text-primary-300"
                      >
                        <div className="w-6 h-6 relative">
                          <img
                            src={
                              isDark
                                ? "/images/ArrowLeft_dark.svg"
                                : "/images/ArrowLeft.svg"
                            }
                            alt=""
                            aria-hidden="true"
                          />
                        </div>
                        {i18n.get("mySavedPlaces")}
                      </button>
                    </div>
                  )}

                  {/* Right areas */}
                  <div className="flex items-center gap-4">
                    <RWebShare
                      data={{
                        text: i18n.get("seeMyFavSociabilityPlacesText"),
                        url:
                          window.location.origin +
                          "/savedplacedetails/" +
                          savedPlaceId +
                          "/user/" +
                          userId,
                        title: i18n.get("seeMyFavSociabilityPlacesTitle"),
                      }}
                      onClick={() => {
                        handleListShare();
                      }}
                    >
                      <button type="button" aria-label="Share List">
                        <img
                          src={
                            isDark
                              ? "/images/share_list_dark.svg"
                              : "/images/share_list.svg"
                          }
                          alt="Share List"
                          className="w-6 h-6 text-primary-800 dark:text-primary-150"
                          aria-hidden="true"
                        />
                      </button>
                    </RWebShare>

                    {isLoggedIn && isUserOwner && savedPlaceId !== "all" && (
                      <div
                        onClick={() => {
                          handleListSettings();
                        }}
                      >
                        <SavePlaceMenu
                          onDeleteClick={() => handleDeleteList()}
                          list={savedList?.saved_places[0]}
                        />
                      </div>
                    )}

                    <div
                      role="button"
                      className="md:hidden"
                      onClick={toggleMobileMap}
                    >
                      {isMapVisible ? (
                        <IoList
                          size={32}
                          className="bg-primary-900 dark:bg-primary-100 rounded-full text-white dark:text-primary-900 p-1"
                        />
                      ) : (
                        <FaRegMap
                          size={32}
                          className="bg-primary-900 dark:bg-primary-100 rounded-full text-white dark:text-primary-900 p-1"
                        />
                      )}
                    </div>
                  </div>
                </div>
                {/* ROW 2 */}
                <div className="py-[16px] px-[32px] flex justify-between items-center">
                  {/* Left area: Heading and description */}
                  <div className="flex flex-col">
                    <h1
                      className="text-primary-800 dark:text-primary-150 desktop-h4-medium-bold"
                      tabIndex={0}
                    >
                      {savedPlaceId === "all"
                        ? i18n.get("allSavedPlaces")
                        : savedList?.saved_places[0]?.name}
                    </h1>
                    <div
                      className="text-natural-700 dark:text-white paragraph-medium-regular py-1"
                      tabIndex={0}
                    >
                      {i18n.get("listDescription")}
                    </div>
                  </div>

                  {/* Sorting dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger>
                      {/* Sort list button */}
                      <div
                        className="h-12 px-4 py-3 bg-primary-50 dark:bg-secondary-800 rounded-xl border border-[#cbc9f2] flex justify-center items-center gap-2 cursor-pointer"
                        aria-haspopup="true" // Indicates a dropdown
                        aria-expanded="false" // Will be set to true when the dropdown is open
                        aria-controls="sort-menu"
                      >
                        <div className="w-6 h-6 relative rounded-[20px]">
                          <img
                            className="w-6 h-6"
                            src={
                              isDark
                                ? "/images/ArrowsDownUp_dark.svg"
                                : "/images/ArrowsDownUp.svg"
                            }
                            alt=""
                            aria-hidden="true"
                          />
                        </div>
                        <div
                          className="text-center text-primary-800 dark:text-primary-150 text-sm font-semibold font-['Inter'] leading-none"
                          aria-label="Sort options"
                        >
                          Sort
                        </div>
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="dark:bg-primary-800 bg-white"
                      align="end"
                      tabIndex={0}
                    >
                      <DropdownMenuLabel tabIndex={0} aria-label="Sort By">
                        Sort By
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuRadioGroup
                        tabIndex={0}
                        value={currentSort}
                        onValueChange={(value) => {
                          handleListSort(value as any);
                        }}
                      >
                        <DropdownMenuRadioItem
                          value="latest"
                          tabIndex={0}
                          className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                        >
                          Latest
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                          value="earliest"
                          tabIndex={0}
                          className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                        >
                          Earliest
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                          value="alphabetical"
                          tabIndex={0}
                          className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                        >
                          Alphabetical
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>
            )}

            {selectedVenue && (
              <>
                {/* ROW 1 */}
                <div className="pl-[32px] pr-[20px] flex justify-between items-center">
                  {/* Left area */}
                  {isLoggedIn && isUserOwner && (
                    <div className="flex items-center">
                      <button
                        aria-label={`Back to ${savedPlaceId === "all" ? i18n.get("allSavedPlaces") : savedList?.saved_places[0]?.name}`}
                        onClick={() => {
                          track.current?.prev();
                          setCurrentVenueTypePage(0);
                          setTimeout(() => {
                            setSelectedVenue(null);
                          }, 500);
                        }}
                        className="flex items-center gap-2 px-1 py-3 bg-white dark:bg-primary-900 rounded-3xl text-center button-small-semi-bold !text-primary-500 dark:!text-primary-300"
                      >
                        <div className="w-6 h-6 relative">
                          <img
                            src={
                              isDark
                                ? "/images/ArrowLeft_dark.svg"
                                : "/images/ArrowLeft.svg"
                            }
                            alt=""
                            aria-hidden="true"
                          />
                        </div>
                        {savedPlaceId === "all"
                          ? i18n.get("allSavedPlaces")
                          : savedList?.saved_places[0]?.name}
                      </button>
                    </div>
                  )}

                  {/* Right areas */}
                  <div className="flex items-center gap-4">
                    &nbsp;
                    <div
                      role="button"
                      className="md:hidden"
                      onClick={toggleMobileMap}
                    >
                      {isMapVisible ? (
                        <IoList
                          size={32}
                          className="bg-primary-900 dark:bg-primary-100 rounded-full text-white dark:text-primary-900 p-1"
                        />
                      ) : (
                        <FaRegMap
                          size={32}
                          className="bg-primary-900 dark:bg-primary-100 rounded-full text-white dark:text-primary-900 p-1"
                        />
                      )}
                    </div>
                  </div>
                </div>
                {/* ROW 2 */}
                <div className="py-[16px] px-[32px] flex justify-between items-center">
                  &nbsp;
                </div>
              </>
            )}

            {/* VENUE LISTING or DETAIL         */}
            <div className={cn("overflow-y-auto pl-[32px] pr-[16px]")}>
              <ViewPager tag="div" tabIndex={-1}>
                <Frame className="frame">
                  <Track
                    ref={track}
                    viewsToShow={1}
                    className="track"
                    swipe={false}
                    accessibility={false}

                    // autoSize={"width"}
                  >
                    <View>
                      {currentVenueTypePage === 0 && (
                        <VenueList onVenueClick={onVenueClick} />
                      )}
                    </View>
                    <View className="">
                      {selectedVenue && (
                        <VenueDetails
                          selectedVenue={selectedVenue}
                          onBack={() => {
                            track.current?.prev();
                            setTimeout(() => {
                              setSelectedVenue(null);
                            }, 500);
                          }}
                        />
                      )}
                    </View>
                  </Track>
                </Frame>
              </ViewPager>
            </div>
          </div>
          <div className="flex-1 px-[16px] py-[20px] w-[50%]">
            <MapView query={query} />
          </div>
        </div>

        {/* MOBILE VIEW */}
        <div className="flex md:hidden flex-col flex-1 w-full">
          {!selectedVenue && (
            <>
              {/* ROW 1 */}
              <div className="px-[16px] my-[16px] flex flex-row items-center">
                <div className="flex-1 mr-1">
                  <div className="flex justify-between items-center">
                    {/* Left area */}
                    {isLoggedIn && isUserOwner && (
                      <div className="flex items-center">
                        <button
                          onClick={() => {
                            AnalyticsService.trackGAEventHandler(
                              "viewListClickedBackButton"
                            );
                            AnalyticsService.trackMx(
                              "viewListClickedBackButton",
                              {}
                            );
                            onBack();
                          }}
                          aria-label={`Back to ${i18n.get("mySavedPlaces")}`}
                          className="flex items-center gap-2 px-1 py-3 bg-white dark:bg-primary-900 rounded-3xl text-center button-small-semi-bold !text-primary-500 dark:!text-primary-300"
                        >
                          <div className="w-6 h-6 relative">
                            <img
                              src={
                                isDark
                                  ? "/images/ArrowLeft_dark.svg"
                                  : "/images/ArrowLeft.svg"
                              }
                              alt=""
                            />
                          </div>
                          {i18n.get("mySavedPlaces")}
                        </button>
                      </div>
                    )}

                    {/* Right areas */}
                    <div className="flex items-center gap-4">
                      <RWebShare
                        data={{
                          text: i18n.get("seeMyFavSociabilityPlacesText"),
                          url:
                            window.location.origin +
                            "/savedplacedetails/" +
                            savedPlaceId +
                            "/user/" +
                            userId,
                          title: i18n.get("seeMyFavSociabilityPlacesTitle"),
                        }}
                        onClick={() => {
                          handleListShare();
                        }}
                      >
                        <button type="button">
                          <img
                            src={
                              isDark
                                ? "/images/share_list_dark.svg"
                                : "/images/share_list.svg"
                            }
                            alt="Share List"
                            className="w-6 h-6 text-primary-800 dark:text-primary-150"
                          />
                        </button>
                      </RWebShare>

                      {isLoggedIn && isUserOwner && savedPlaceId !== "all" && (
                        <SavePlaceMenu
                          onDeleteClick={() => handleDeleteList()}
                          list={savedList?.saved_places[0]}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div
                  onClick={() => {
                    setMobileListView(!mobileListView);
                  }}
                  className="p-2 mx-1 bg-primary-50 rounded-full"
                >
                  {mobileListView ? (
                    <MapTrifold size={24} className="text-primary-900" />
                  ) : (
                    <ListBullets size={24} className="text-primary-900" />
                  )}
                </div>
              </div>

              {/* ROW 2     */}
              {mobileListView && (
                <div className="px-[16px] pb-[16px] flex justify-between items-center">
                  {/* Left area: Heading and description */}
                  <div className="flex flex-col">
                    <h1 className="text-primary-800 dark:text-primary-150 desktop-h4-medium-bold">
                      {savedPlaceId === "all"
                        ? i18n.get("allSavedPlaces")
                        : savedList?.saved_places[0]?.name}
                    </h1>
                    <div className="text-natural-700 dark:text-white paragraph-medium-regular py-1">
                      {i18n.get("listDescription")}
                    </div>
                  </div>

                  {/* Sorting dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger>
                      {/* Sort list button */}
                      <div className="h-12 px-4 py-3 bg-primary-50 dark:bg-secondary-800 rounded-xl border border-[#cbc9f2] flex justify-center items-center gap-2 cursor-pointer">
                        <div className="w-6 h-6 relative rounded-[20px]">
                          <img
                            className="w-6 h-6"
                            src={
                              isDark
                                ? "/images/ArrowsDownUp_dark.svg"
                                : "/images/ArrowsDownUp.svg"
                            }
                            alt=""
                          />
                        </div>
                        <div className="text-center text-primary-800 dark:text-primary-150 text-sm font-semibold font-['Inter'] leading-none">
                          Sort
                        </div>
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="dark:bg-primary-800 bg-white"
                      align="end"
                    >
                      <DropdownMenuLabel>Sort By</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuRadioGroup
                        value={currentSort}
                        onValueChange={(value) => {
                          setCurrentSort(value as any);
                        }}
                      >
                        <DropdownMenuRadioItem
                          value="latest"
                          className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                        >
                          Latest
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                          value="earliest"
                          className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                        >
                          Earliest
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem
                          value="alphabetical"
                          className="hover:bg-gray-200 focus:bg-gray-200 dark:hover:bg-primary-700 dark:focus:bg-primary-700 rounded-md mx-2 my-1"
                        >
                          Alphabetical
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </>
          )}

          {selectedVenue && (
            <>
              {/* ROW 1 */}
              <div className="px-[16px] my-[16px] flex flex-row items-center">
                <div className="flex-1 mr-1">
                  <div className="flex justify-between items-center">
                    {/* Left area */}
                    {isLoggedIn && isUserOwner && (
                      <div className="flex items-center">
                        <button
                          onClick={() => {
                            track.current?.prev();
                            setCurrentVenueTypePage(0);
                            setTimeout(() => {
                              setSelectedVenue(null);
                            }, 500);
                          }}
                          className="flex items-center gap-2 px-1 py-3 bg-white dark:bg-primary-900 rounded-3xl text-center button-small-semi-bold !text-primary-500 dark:!text-primary-300"
                        >
                          <div className="w-6 h-6 relative">
                            <img
                              src={
                                isDark
                                  ? "/images/ArrowLeft_dark.svg"
                                  : "/images/ArrowLeft.svg"
                              }
                              alt=""
                            />
                          </div>
                          {savedPlaceId === "all"
                            ? i18n.get("allSavedPlaces")
                            : savedList?.saved_places[0]?.name}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  onClick={() => {
                    setMobileListView(!mobileListView);
                  }}
                  className="p-2 mx-1 bg-primary-50 rounded-full"
                >
                  {mobileListView ? (
                    <MapTrifold size={24} className="text-primary-900" />
                  ) : (
                    <ListBullets size={24} className="text-primary-900" />
                  )}
                </div>
              </div>

              {/* ROW 2     */}
              {mobileListView && (
                <div className="px-[16px] pb-[16px] flex justify-between items-center">
                  &nbsp;
                </div>
              )}
            </>
          )}

          {/* MOBILE VENUE LISTING or DETAIL         */}
          <div
            className={cn(
              "flex-1 px-[16px] pb-[20px]",
              mobileListView ? "overflow-y-scroll" : ""
            )}
          >
            {mobileListView ? (
              <ViewPager tag="div" tabIndex={-1}>
                <Frame className="frame">
                  <Track
                    ref={track}
                    viewsToShow={1}
                    className="track"
                    swipe={false}
                    accessibility={false}

                    // autoSize={"width"}
                  >
                    <View>
                      {currentVenueTypePage === 0 && (
                        <VenueList onVenueClick={onVenueClick} />
                      )}
                    </View>
                    <View className="">
                      {selectedVenue && (
                        <VenueDetails
                          selectedVenue={selectedVenue}
                          onBack={() => {
                            track.current?.prev();
                            setCurrentVenueTypePage(0);
                            setTimeout(() => {
                              setSelectedVenue(null);
                            }, 500);
                          }}
                        />
                      )}
                    </View>
                  </Track>
                </Frame>
              </ViewPager>
            ) : (
              <MapView query={query} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
